import "@/App.css";
import { useState, useRef, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import {
  Phone,
  MapPin,
  Clock,
  Check,
  Sparkles,
  Shield,
  Eye,
  ChevronDown,
  MessageCircle,
  ArrowRight,
  Zap } from
"lucide-react";

const WHATSAPP_NUMBER = "+393479393361";
const WHATSAPP_MESSAGE = encodeURIComponent("Ciao! Vorrei prenotare un appuntamento con lo sconto del 50%.");
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${WHATSAPP_MESSAGE}`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/6fyvoo1o_RETICO-12.png";

// Assets - Real photos
const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/zzdeagmd_PA_00526.jpg",
  cut: "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/0ube3z9r_PA_00506.jpg",
  beard: "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/zbvahq52_PA_09333%20%281%29.jpg",
  patchBefore: "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/5s6ewtgu_envato-labs-image-edit%20%2868%29.png",
  patchAfter: "https://customer-assets.emergentagent.com/job_precision-barber-6/artifacts/afd9enev_envato-labs-image-edit%20%2869%29.png"
};

// Before/After Image Comparison Slider - VERTICAL FULL HEIGHT
const BeforeAfterSlider = ({ beforeImage, afterImage, beforeLabel = "PRIMA", afterLabel = "DOPO" }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientY) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const y = clientY - rect.top;
    const percentage = Math.max(0, Math.min(100, y / rect.height * 100));
    setSliderPosition(percentage);
  };

  const handleStart = (e) => {
    isDragging.current = true;
    // Prevent page scroll on touch devices
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
  };

  const handleEnd = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleMove(e.clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging.current) {
      e.preventDefault();
      handleMove(e.touches[0].clientY);
    }
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    e.preventDefault();
    handleMove(e.touches[0].clientY);
  };

  useEffect(() => {
    const container = containerRef.current;

    // Mouse events
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('mousemove', handleMouseMove);

    // Touch events with passive: false to allow preventDefault
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('mousemove', handleMouseMove);
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleEnd);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-md mx-auto aspect-[3/4] overflow-hidden select-none neon-border"
      style={{ touchAction: 'none' }}
      onMouseDown={handleStart}
      data-testid="before-after-slider">

      {/* After Image (Background - bottom) */}
      <div className="absolute inset-0">
        <img
          src={afterImage}
          alt="Dopo"
          className="w-full h-full object-cover object-center pointer-events-none"
          draggable="false" />

        <div className="absolute bottom-4 right-4 neon-btn-filled px-4 py-2 text-sm font-body font-semibold tracking-wider">
          {afterLabel}
        </div>
      </div>
      
      {/* Before Image (Clipped - top) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 ${100 - sliderPosition}% 0)` }}>

        <img
          src={beforeImage}
          alt="Prima"
          className="w-full h-full object-cover object-center pointer-events-none"
          draggable="false" />

        <div className="absolute top-4 left-4 bg-black/80 text-white border border-white/30 px-4 py-2 text-sm font-body font-semibold tracking-wider">
          {beforeLabel}
        </div>
      </div>
      
      {/* Slider Handle - Horizontal */}
      <div
        className="absolute left-0 right-0 h-[3px] bg-[#1d8cc8] z-10"
        style={{
          top: `${sliderPosition}%`,
          transform: 'translateY(-50%)',
          boxShadow: '0 0 10px #1d8cc8, 0 0 20px #1d8cc8, 0 0 30px #1d8cc8'
        }}>

        {/* Handle Circle - Larger for mobile */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 md:w-12 md:h-12 bg-black rounded-full flex items-center justify-center neon-border cursor-ns-resize">
          <div className="flex flex-col items-center gap-0.5">
            <ChevronDown className="rotate-180 neon-icon" size={16} />
            <ChevronDown className="neon-icon" size={16} />
          </div>
        </div>
      </div>
      
      {/* Mobile instruction overlay - shows briefly */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/70 text-white/80 px-4 py-2 rounded-full text-xs font-body md:hidden pointer-events-none">
        Scorri su e giù per confrontare
      </div>
    </div>);

};

// Floating WhatsApp Button
const WhatsAppButton = () =>
<a
  href={WHATSAPP_LINK}
  target="_blank"
  rel="noopener noreferrer"
  data-testid="whatsapp-float-btn"
  className="whatsapp-float flex items-center justify-center"
  aria-label="Contattaci su WhatsApp">

    <MessageCircle size={26} fill="white" color="white" />
  </a>;


// Neon CTA Button Component
const CTAButton = ({ children, className = "", variant = "filled", ...props }) => {
  const baseClass = variant === "filled" ?
  "neon-btn-filled" :
  "neon-btn";

  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 font-body font-semibold tracking-wide px-8 py-4 transition-all duration-300 ${baseClass} ${className}`}
      {...props}>

      {children}
      <ArrowRight size={18} />
    </a>);

};

// Neon Number Component
const NeonNumber = ({ number, size = "large" }) => {
  const sizeClasses = size === "large" ?
  "text-7xl md:text-8xl lg:text-9xl" :
  "text-5xl md:text-6xl";

  return (
    <span className={`font-heading ${sizeClasses} neon-number animate-number-glow`}>
      {number}
    </span>);

};

// Hero Section - Impactful Neon
const HeroSection = () =>
<section
  data-testid="hero-section"
  className="relative min-h-screen flex items-center bg-black overflow-hidden">

    {/* Background Image */}
    <div
    className="absolute inset-0 bg-cover bg-center"
    style={{ backgroundImage: `url(${ASSETS.hero})` }} />

    
    {/* Dark Overlay */}
    <div className="absolute inset-0 bg-black/60" />
    
    {/* Noise texture */}
    <div className="absolute inset-0 noise-overlay" />
    
    {/* Neon accent lines */}
    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#1d8cc8] to-transparent opacity-50"
  style={{ boxShadow: '0 0 20px #1d8cc8, 0 0 40px #1d8cc8' }} />
    
    {/* Content */}
    <div className="relative z-10 section-container py-24 lg:py-32 w-full">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12">
        {/* Left side - Text content */}
        <div className="lg:w-1/2">
          {/* Offer Badge */}
          <div className="inline-flex items-center gap-2 neon-btn px-5 py-2 mb-8 animate-neon-pulse">
            <Zap size={16} className="neon-icon" />
            <span className="font-body font-semibold text-sm tracking-widest">SOLO PER I PRIMI 100 CLIENTI</span>
          </div>
          
          {/* Headline */}
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-none mb-4">
            50% DI SCONTO
          </h1>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl neon-text leading-none mb-8">
            SUL PRIMO TAGLIO
          </h2>
          <p className="font-heading text-2xl text-white/60 mb-8">O TAGLIO + BARBA</p>
          
          {/* Subheadline */}
          <p className="font-body text-lg text-white/70 mb-10 max-w-lg leading-relaxed">
            Se cerchi precisione, igiene impeccabile e un servizio costruito su misura per te, questa è la tua barberia di riferimento.
          </p>
          
          {/* CTA */}
          <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-cta"
          className="inline-flex items-center gap-3 font-body font-semibold tracking-wide px-8 py-4 transition-all duration-300 bg-white text-[#1d8cc8] hover:bg-gray-100 animate-pulse-cta-hero"
          style={{
            boxShadow: '0 0 15px rgba(29, 140, 200, 0.6), 0 0 30px rgba(29, 140, 200, 0.4), 0 0 50px rgba(29, 140, 200, 0.2)'
          }}>

            PRENOTA ORA SU WHATSAPP
            <ArrowRight size={18} />
          </a>
        </div>
        
        {/* Right side - Big Logo */}
        <div className="hidden lg:flex lg:w-1/2 lg:justify-end lg:items-center">
          <img
          src={LOGO_URL}
          alt="Barberia Retico"
          className="h-64 xl:h-80 opacity-90"
          data-testid="hero-logo"
          style={{ filter: 'drop-shadow(0 0 30px rgba(29, 140, 200, 0.3))' }} />

        </div>
      </div>
      
      {/* Mobile Logo */}
      <div className="lg:hidden mt-12 flex justify-center">
        <img
        src={LOGO_URL}
        alt="Barberia Retico"
        className="h-32"
        data-testid="hero-logo-mobile" />

      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="neon-icon" />
      </div>
    </div>
  </section>;


// Positioning Section
const PositioningSection = () =>
<section data-testid="positioning-section" className="py-24 lg:py-32 bg-[#0a0a0a] relative">
    <div className="absolute top-0 left-0 w-full h-[1px] glow-line" />
    
    <div className="section-container">
      <div className="max-w-4xl">
        <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// LA NOSTRA FILOSOFIA</p>
        
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
          LA NUOVA BARBERIA DI CINECITTÀ
        </h2>
        <h3 className="font-heading text-3xl md:text-4xl neon-text mb-10">
          PER CHI PRETENDE STANDARD ALTI
        </h3>
        
        <p className="font-body text-lg text-white/60 mb-12 leading-relaxed max-w-2xl">
          Barberia Retico nasce con un'idea chiara: offrire un servizio maschile essenziale, tecnico e curato nei minimi dettagli.
        </p>
        
        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          {[
        "Esperienza decennale",
        "Standard operativi chiari",
        "Igiene rigorosa",
        "Ambiente nuovo e professionale",
        "Prodotti proprietari",
        "Ascolto reale"].
        map((item, i) =>
        <div key={i} className="flex items-center gap-4 group">
              <Check className="neon-icon group-hover:animate-neon-pulse" size={20} />
              <span className="font-body text-white group-hover:text-[#1d8cc8] transition-colors">{item}</span>
            </div>
        )}
        </div>
        
        <div className="border-l-2 border-[#1d8cc8] pl-6" style={{ boxShadow: '-2px 0 20px rgba(29, 140, 200, 0.3)' }}>
          <p className="font-heading text-2xl md:text-3xl text-white">
            QUI NON ENTRI PER "DARE UNA SISTEMATA".<br />
            <span className="neon-text">ENTRI PER UN RISULTATO PRECISO.</span>
          </p>
        </div>
      </div>
    </div>
  </section>;


// Differentiation Section
const DifferentiationSection = () =>
<section data-testid="differentiation-section" className="py-24 lg:py-32 bg-black relative">
    <div className="section-container">
      <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// PERCHÉ SCEGLIERCI</p>
      
      <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-16">
        PERCHÉ I CLIENTI <span className="neon-text">CI SCELGONO</span>
      </h2>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
      { icon: Shield, text: "Igiene maniacale" },
      { icon: MessageCircle, text: "Consulenza prima di ogni servizio" },
      { icon: Check, text: "Tecnica e precisione reale" },
      { icon: Zap, text: "Nessuna improvvisazione" }].
      map((item, i) =>
      <div
        key={i}
        className="card-neon p-6 flex items-start gap-4">

            <item.icon className="neon-icon flex-shrink-0" size={24} />
            <span className="font-body text-white text-lg">{item.text}</span>
          </div>
      )}
      </div>
      
      <p className="mt-12 font-body text-lg text-white/50">
        Chi ha già provato esperienze mediocri capisce la differenza dal primo appuntamento.
      </p>
    </div>
  </section>;


// Service Taglio Section - Impactful Numbers
const TaglioSection = () =>
<section data-testid="taglio-section" className="py-24 lg:py-32 bg-[#0a0a0a] relative">
    <div className="absolute top-0 left-0 w-full h-[1px] glow-line" />
    
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// TAGLIO RETICO</p>
          
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
            IL TAGLIO NON INIZIA
          </h2>
          <h3 className="font-heading text-3xl md:text-4xl text-white mb-2">CON LE FORBICI.</h3>
          <h3 className="font-heading text-3xl md:text-4xl neon-text mb-10">INIZIA CON L'ASCOLTO.</h3>
          
          <p className="font-body text-white/60 mb-10 leading-relaxed">
            Prima di iniziare, facciamo una consulenza precisa: capire cosa desideri, cosa ti valorizza, cosa è coerente con il tuo stile.
          </p>
          
          {/* Impactful numbered steps */}
          <div className="space-y-6 mb-10">
            {[
          "Shampoo iniziale",
          "Taglio con attenzione ai dettagli",
          "Rifiniture tecniche",
          "Shampoo finale",
          "Styling professionale",
          "Verifica finale"].
          map((step, i) =>
          <div key={i} className="flex items-center gap-6 group">
                <span className="step-number w-16 text-center group-hover:animate-neon-pulse">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-body text-white text-lg group-hover:text-[#1d8cc8] transition-colors">{step}</span>
              </div>
          )}
          </div>
          
          <p className="font-heading text-2xl text-white mb-8 border-l-2 border-[#1d8cc8] pl-4" style={{ boxShadow: '-2px 0 15px rgba(29, 140, 200, 0.3)' }}>
            NON ESCI FINCHÉ IL RISULTATO<br />
            <span className="neon-text">NON È CORRETTO.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="neon-btn-filled px-6 py-3 font-body font-semibold">
              50% SUL PRIMO TAGLIO
            </div>
            <CTAButton variant="outline" data-testid="taglio-cta">
              PRENOTA SU WHATSAPP
            </CTAButton>
          </div>
        </div>
        
        <div className="order-1 lg:order-2">
          <div className="relative">
            <img
            src={ASSETS.cut}
            alt="Servizio Taglio"
            className="w-full h-[400px] lg:h-[550px] object-cover" />

            {/* Neon frame effect */}
            <div className="absolute inset-0 border-2 border-[#1d8cc8] opacity-50 pointer-events-none"
          style={{ boxShadow: 'inset 0 0 30px rgba(29, 140, 200, 0.2)' }} />
          </div>
        </div>
      </div>
    </div>
  </section>;


// Beard Ceremony Section - Background Image
const BeardSection = () =>
<section data-testid="beard-section" className="py-24 lg:py-32 relative min-h-[700px] flex items-center overflow-hidden">
    {/* Background Image */}
    <div
    className="absolute inset-0 bg-cover bg-center"
    style={{ backgroundImage: `url(${ASSETS.beard})` }} />

    
    {/* Dark Overlay */}
    <div className="absolute inset-0 bg-black/70" />
    
    {/* Noise texture */}
    <div className="absolute inset-0 noise-overlay" />
    
    <div className="section-container relative z-10">
      <div className="max-w-2xl">
        <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// BEARD EXPERIENCE</p>
        
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">IL NOSTRO BEARD EXPERIENCE:

      </h2>
        <h3 className="font-heading text-3xl md:text-4xl neon-text mb-10">ESPERIENZA COMPLETA</h3>
        
        <p className="font-body text-white/70 mb-10 leading-relaxed">
          Un servizio pensato per chi non vuole solo "regolare" la barba.
        </p>
        
        {/* Numbered ritual steps */}
        <div className="space-y-4 mb-10">
          {[
        "Benda sugli occhi per il relax",
        "Vaporizzatore per aprire i pori",
        "Panni caldi e freddi",
        "Shampoo e balsamo specifici",
        "Olio da barba nutriente",
        "Rifinitura tecnica",
        "Dopo barba alcolico"].
        map((step, i) =>
        <div key={i} className="flex items-center gap-4 group">
              <span className="font-heading text-2xl neon-number w-8">{i + 1}</span>
              <Sparkles className="neon-icon flex-shrink-0" size={16} />
              <span className="font-body text-white group-hover:text-[#1d8cc8] transition-colors">{step}</span>
            </div>
        )}
        </div>
        
        <div className="border-l-2 border-[#1d8cc8] pl-4 bg-black/30 py-4 pr-4" style={{ boxShadow: '-2px 0 15px rgba(29, 140, 200, 0.3)' }}>
          <p className="font-heading text-2xl text-white">
            NON È UN SERVIZIO VELOCE.<br />
            <span className="neon-text">È UN RITUALE.</span>
          </p>
        </div>
      </div>
    </div>
  </section>;


// Patch Section
const PatchSection = () =>
<section data-testid="patch-section" className="py-24 lg:py-32 bg-[#0a0a0a] relative">
    <div className="absolute top-0 left-0 w-full h-[1px] glow-line" />
    
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// SOLUZIONE AVANZATA</p>
          
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
            DIRADAMENTO?
          </h2>
          <h3 className="font-heading text-3xl md:text-4xl neon-text mb-10">NON SERVE UN TRAPIANTO.</h3>
          
          <p className="font-body text-white/60 mb-8 leading-relaxed">
            Il Retico Density System è una soluzione estetica avanzata per chi:
          </p>
          
          <div className="space-y-3 mb-8">
            {[
          "Ha zone diradate",
          "Vuole un risultato immediato",
          "Non vuole interventi chirurgici",
          "Cerca una soluzione naturale"].
          map((item, i) =>
          <div key={i} className="flex items-center gap-3 group">
                <Check className="neon-icon" size={18} />
                <span className="font-body text-white group-hover:text-[#1d8cc8] transition-colors">{item}</span>
              </div>
          )}
          </div>
          
          <div className="card-neon p-6 mb-8">
            <h3 className="font-heading text-2xl text-white mb-4">COS'È DAVVERO?</h3>
            <p className="font-body text-white/60 mb-4 leading-relaxed">
              È un sistema personalizzato che ricostruisce visivamente la densità dei capelli in modo realistico.
            </p>
            <div className="space-y-2">
              <p className="font-body text-white font-semibold">Non è una parrucca.</p>
              <p className="font-body text-white font-semibold">Non è un trucco temporaneo.</p>
              <p className="font-body text-white font-semibold">Non è invasiva.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
          "Valutazione personalizzata",
          "Applicazione professionale",
          "Massima discrezione",
          "Manutenzione chiara"].
          map((item, i) =>
          <div key={i} className="flex items-center gap-2">
                <Eye className="neon-icon flex-shrink-0" size={16} />
                <span className="font-body text-sm text-white/80">{item}</span>
              </div>
          )}
          </div>
          
          <CTAButton data-testid="patch-cta">
            RICHIEDI CONSULENZA RISERVATA
          </CTAButton>
        </div>
        
        <div className="order-1 lg:order-2 flex justify-center">
          <BeforeAfterSlider
          beforeImage={ASSETS.patchBefore}
          afterImage={ASSETS.patchAfter} />

        </div>
      </div>
    </div>
  </section>;


// Urgency Section - Big Impact
const UrgencySection = () =>
<section data-testid="urgency-section" className="py-20 bg-black relative overflow-hidden">
    {/* Neon glow background */}
    <div className="absolute inset-0 flex items-center justify-center opacity-20">
      <div className="w-[600px] h-[600px] rounded-full bg-[#1d8cc8] blur-[150px]" />
    </div>
    
    <div className="section-container relative z-10">
      <div className="text-center max-w-3xl mx-auto">
        <div className="mb-8">
          <NeonNumber number="60" />
        </div>
        
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
          RESTANO SOLO <span className="neon-text">60 POSTI</span>
        </h2>
        <h3 className="font-heading text-2xl md:text-3xl text-white/80 mb-8">
          CON IL 50% DI SCONTO
        </h3>
        
        <p className="font-body text-lg text-white/60 mb-6 leading-relaxed">
          È una promozione reale per celebrare la nuova apertura.
          Quando i posti saranno terminati, lo sconto non sarà più disponibile.
        </p>
        
        <p className="font-body text-white font-semibold mb-10">
          Se stai leggendo questa pagina, puoi ancora approfittarne.
        </p>
        
        <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="urgency-cta"
        className="inline-flex items-center gap-3 neon-btn-filled px-12 py-5 font-body font-bold text-lg tracking-wide animate-neon-pulse">

          PRENOTA ORA SU WHATSAPP
          <ArrowRight size={20} />
        </a>
      </div>
    </div>
  </section>;


// Location Section
const LocationSection = () =>
<section data-testid="location-section" className="py-24 lg:py-32 bg-[#0a0a0a] relative">
    <div className="absolute top-0 left-0 w-full h-[1px] glow-line" />
    
    <div className="section-container">
      <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// DOVE TROVARCI</p>
      
      <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-12">
        CI TROVI IN
      </h2>
      
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Map */}
        <div className="h-80 lg:h-[400px] neon-border overflow-hidden">
          <iframe
          title="Barberia Retico Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2972.7!2d12.5584!3d41.8518!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sPiazza+di+Cinecitt%C3%A0+47%2C+Roma!5e0!3m2!1sit!2sit!4v1700000000000"
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade" />

        </div>
        
        {/* Info */}
        <div className="space-y-8">
          <div className="card-neon p-6 flex items-start gap-5">
            <div className="w-14 h-14 neon-btn-filled flex items-center justify-center flex-shrink-0">
              <MapPin className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-heading text-2xl text-white mb-2">INDIRIZZO</h3>
              <p className="font-body text-white/60">
                Piazza di Cinecittà 47<br />
                00174 – Roma
              </p>
              <p className="font-body text-white/80 mt-2">
                A pochi minuti da casa.
              </p>
            </div>
          </div>
          
          <div className="card-neon p-6 flex items-start gap-5">
            <div className="w-14 h-14 bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
              <Clock className="neon-icon" size={24} />
            </div>
            <div>
              <h3 className="font-heading text-2xl text-white mb-2">ORARI</h3>
              <p className="font-body text-white/60">
                Dal Martedì al Sabato<br />
                <span className="text-white font-semibold">09:00 - 19:00</span>
              </p>
            </div>
          </div>
          
          <div className="card-neon p-6 flex items-start gap-5">
            <div className="w-14 h-14 neon-btn flex items-center justify-center flex-shrink-0">
              <Phone className="neon-icon" size={24} />
            </div>
            <div>
              <h3 className="font-heading text-2xl text-white mb-2">WHATSAPP</h3>
              <a
              href={`tel:${WHATSAPP_NUMBER}`}
              className="font-body neon-text hover:underline text-xl">

                {WHATSAPP_NUMBER}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;


// Booking Section - WhatsApp CTA with Neon
const BookingSection = () => {
  return (
    <section data-testid="booking-section" className="py-24 lg:py-32 bg-black relative overflow-hidden">
      {/* Neon glow background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-[#25D366] blur-[200px] opacity-20" />
      
      <div className="section-container relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-body neon-text text-sm tracking-[0.3em] mb-6">// PRENOTA ORA</p>
          
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
            PRENOTA IL TUO APPUNTAMENTO
          </h2>
          <h3 className="font-heading text-2xl md:text-3xl neon-text mb-8">
            CON IL 50% DI SCONTO
          </h3>
          
          <p className="font-body text-white/60 mb-12 text-lg leading-relaxed">
            Scrivici direttamente su WhatsApp per prenotare il tuo posto.
          </p>
          
          {/* Animated WhatsApp Button */}
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-whatsapp-cta"
            className="group relative inline-flex items-center gap-4 px-12 py-6 text-white font-body font-bold text-xl tracking-wide rounded-full overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95"
            style={{
              background: '#000000',
              boxShadow: '0 0 20px rgba(37, 211, 102, 0.6), 0 0 40px rgba(37, 211, 102, 0.4), 0 0 60px rgba(37, 211, 102, 0.2)'
            }}>

            {/* Button content */}
            <MessageCircle size={28} fill="white" className="animate-bounce-subtle" />
            <span>SCRIVICI SU WHATSAPP</span>
          </a>
          
          <p className="font-body text-white/40 text-sm mt-10">
            Ti rispondiamo rapidamente per confermare l'appuntamento.
          </p>
          
          {/* Phone number display */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <Phone size={16} className="neon-icon" />
            <span className="font-body neon-text font-medium">{WHATSAPP_NUMBER}</span>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () =>
<footer className="py-12 bg-[#050505] border-t border-white/10">
    <div className="section-container">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <img
        src={LOGO_URL}
        alt="Barberia Retico"
        className="h-12" />

        <p className="font-body text-white/40 text-sm">
          © {new Date().getFullYear()} Barberia Retico – Piazza di Cinecittà 47, Roma
        </p>
      </div>
    </div>
  </footer>;


// Main App
function App() {
  return (
    <div className="App bg-black min-h-screen">
      <Toaster position="top-center" richColors />
      <WhatsAppButton />
      
      <HeroSection />
      <PositioningSection />
      <DifferentiationSection />
      <TaglioSection />
      <BeardSection />
      <PatchSection />
      <UrgencySection />
      <LocationSection />
      <BookingSection />
      <Footer />
    </div>);

}

export default App;